import React from 'react';

import { getHolidaysForDate } from '../../../utils/holidayService';

const getActivityStyle = (activity, persons) => {
  const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
  const personColors = activity?.personColors || (activity?.personColor ? [activity?.personColor] : []);
  
  if (personIds?.length === 0 || personColors?.length === 0) {
    return {
      backgroundColor: '#e5e7eb20',
      borderLeft: '3px solid #e5e7eb'
    };
  }
  
  const allIds = persons?.map(p => p?.id) || [];
  const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
  
  if (allSelected) {
    return {
      background: 'linear-gradient(90deg, #ef4444, #f59e0b, #10b981, #3b82f6, #6366f1, #a855f7)',
      borderLeft: '3px solid #a855f7',
      backgroundSize: '200% 100%',
      animation: 'rainbow-shift 3s ease infinite'
    };
  } else if (personColors?.length === 1) {
    return {
      backgroundColor: `${personColors?.[0]}20`,
      borderLeft: `3px solid ${personColors?.[0]}`
    };
  } else {
    const gradientStops = personColors?.map((color, idx) => {
      const position = (idx / (personColors?.length - 1)) * 100;
      return `${color} ${position}%`;
    })?.join(', ');
    
    return {
      background: `linear-gradient(90deg, ${gradientStops})`,
      borderLeft: `3px solid ${personColors?.[0]}`,
      opacity: 0.9
    };
  }
};

const getActivityTextColor = (activity, persons) => {
  const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
  const allIds = persons?.map(p => p?.id) || [];
  const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
  
  if (allSelected || (activity?.personColors?.length > 1)) {
    return '#ffffff';
  }
  
  return activity?.personColor || activity?.personColors?.[0] || '#000000';
};

const WeekView = ({ 
  currentDate, 
  activities, 
  onActivityClick,
  onDateClick,
  onDragStart,
  onDrop,
  preferences,
  persons = []
}) => {
  const getWeekDays = () => {
    const weekStart = new Date(currentDate);
    weekStart?.setDate(currentDate?.getDate() - currentDate?.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day?.setDate(weekStart?.getDate() + i);
      days?.push(day);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  const isToday = (date) => {
    const today = new Date();
    return date?.getDate() === today?.getDate() &&
           date?.getMonth() === today?.getMonth() &&
           date?.getFullYear() === today?.getFullYear();
  };

  const getActivitiesForDate = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    return activities?.filter(activity => activity?.date === dateStr);
  };

  const getHolidaysForDay = (date) => {
    return getHolidaysForDate(
      date,
      preferences?.holidayLocation,
      preferences?.showPublicHolidays,
      preferences?.showSchoolTerms
    );
  };

  const getActivitiesForDateTime = (date, hour) => {
    return activities?.filter(activity => {
      const activityDate = new Date(activity.date);
      const activityHour = parseInt(activity?.time?.split(':')?.[0]);
      
      return activityDate?.getDate() === date?.getDate() &&
             activityDate?.getMonth() === date?.getMonth() &&
             activityDate?.getFullYear() === date?.getFullYear() &&
             activityHour === hour;
    });
  };

  const formatTime = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour} ${period}`;
  };

  const handleDrop = (e, date, hour) => {
    e?.preventDefault();
    e?.currentTarget?.classList?.remove('bg-muted');
    const newDate = new Date(date);
    newDate?.setHours(hour, 0, 0, 0);
    onDrop(e, newDate);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.currentTarget?.classList?.add('bg-muted');
  };

  const handleDragLeave = (e) => {
    e?.currentTarget?.classList?.remove('bg-muted');
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm-md overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-8 border-b border-border bg-muted/50 sticky top-0 z-10">
            <div className="p-3 md:p-4 text-xs md:text-sm font-semibold text-muted-foreground">
              Time
            </div>
            {weekDays?.map((date, index) => {
              const dayActivities = getActivitiesForDate(date);
              const dayHolidays = getHolidaysForDay(date);
              const isTodayDate = isToday(date);
              
              return (
                <div 
                  key={date?.toISOString()}
                  className={`p-3 md:p-4 text-center ${isTodayDate ? 'bg-primary/10' : ''}`}
                >
                  <div className="text-xs md:text-sm text-muted-foreground">
                    {date?.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>

                  {/* Holiday Indicators */}
                  {dayHolidays?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {dayHolidays?.map((holiday, idx) => (
                        <div
                          key={idx}
                          className={`text-[10px] px-1.5 py-0.5 rounded truncate ${
                            holiday?.type === 'public' ?'bg-red-100 text-red-700 border border-red-200' 
                              : holiday?.type === 'school' ?'bg-blue-100 text-blue-700 border border-blue-200' :'bg-yellow-100 text-yellow-700 border border-yellow-200'
                          }`}
                          title={holiday?.name}
                        >
                          {holiday?.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="divide-y divide-border">
            {timeSlots?.map((hour) => (
              <div key={hour} className="grid grid-cols-8 min-h-[60px] md:min-h-[80px]">
                <div className="p-2 md:p-3 text-xs text-muted-foreground border-r border-border">
                  {formatTime(hour)}
                </div>
                {weekDays?.map((day) => {
                  const dayActivities = getActivitiesForDateTime(day, hour);
                  
                  return (
                    <div
                      key={`${day?.toISOString()}-${hour}`}
                      className="border-r border-border p-1 md:p-2 hover:bg-muted/50 transition-colors cursor-pointer"
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, day, hour)}
                    >
                      <div className="space-y-1">
                        {dayActivities?.map((activity) => {
                          const activityStyle = getActivityStyle(activity, persons);
                          const textColor = getActivityTextColor(activity, persons);
                          const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
                          const personNames = activity?.personNames || (activity?.personName ? [activity?.personName] : []);
                          const allIds = persons?.map(p => p?.id) || [];
                          const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
                          
                          return (
                            <div
                              key={activity?.id}
                              className="text-xs p-1.5 md:p-2 rounded cursor-move transition-transform hover:scale-105"
                              style={activityStyle}
                              draggable
                              onDragStart={(e) => onDragStart(e, activity)}
                              onClick={(e) => {
                                e?.stopPropagation();
                                onActivityClick(activity);
                              }}
                            >
                              <div className="font-medium truncate flex items-center gap-1" style={{ color: textColor }}>
                                {activity?.title}
                                {allSelected && <span className="text-[10px]">✨</span>}
                              </div>
                              <div className="text-[10px] truncate" style={{ color: textColor, opacity: 0.8 }}>
                                {allSelected ? 'All Family' : personNames?.join(', ')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekView;